'use client';

import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';
import NextImage from 'next/image';
import { useState } from 'react';
import { getImageLoader } from '../../lib/image-loader';

/**
 * Bundles the `sizes` hint with the corner radius, so call sites pick a role
 * rather than hand-rolling both. Each role takes its radius from the theme's
 * semantic tokens, so images round off the way the work group's theme does.
 */
export type ImageBoxVariant = 'thumbnail' | 'card' | 'hero' | 'dialog';
const variantSizes: Record<ImageBoxVariant, string> = {
  // 48-96px squares in lists, carts and receipts.
  thumbnail: '96px',
  // Fills the width of a card, one to three per row.
  card: '(max-width: 599px) 100vw, (max-width: 899px) 50vw, 420px',
  // Split-screen page artwork. Usually the LCP element.
  hero: '(max-width: 899px) 100vw, 620px',
  // Split-dialog artwork. The image column is 10/17 of a paper capped at
  // 1200px, less its padding, and it collapses to full width below `sm`.
  dialog: '(max-width: 599px) 100vw, 640px'
};
type RadiusValue = number | string;
const toCssLength = (value: RadiusValue) => typeof value === 'number' ? `${value}px` : value;

// Thumbnails and card images sit inside a container, so they take the
// smaller form-item radius; page and dialog artwork is a container itself.
// A theme without the radius tokens (a bare MUI theme) falls back to `shape`.
const radius = (theme: Theme, token: 'formItemRadius' | 'containerRadius', size: 'main' | 'mobile') => toCssLength(theme[token]?.[size] ?? theme.shape.borderRadius);
const variantRadius: Record<ImageBoxVariant, (theme: Theme) => string | {
  xs: string;
  sm: string;
}> = {
  thumbnail: theme => radius(theme, 'formItemRadius', 'main'),
  card: theme => radius(theme, 'formItemRadius', 'main'),
  hero: theme => ({
    xs: radius(theme, 'containerRadius', 'mobile'),
    sm: radius(theme, 'containerRadius', 'main')
  }),
  dialog: theme => radius(theme, 'containerRadius', 'main')
};
export interface ImageBoxProps {
  imageUrl?: string | null;
  /** Pass an empty string for artwork already described by adjacent text. */
  altText: string;
  variant?: ImageBoxVariant;
  /** Overrides the variant's `sizes` hint. */
  sizes?: string;
  /** Loads eagerly at high priority. Above-the-fold artwork only. */
  priority?: boolean;
  objectFit?: 'cover' | 'contain';
  /** Forces the original through untouched. */
  unoptimized?: boolean;
  /** Applied to the wrapper — width, height, aspectRatio, radius, bgcolor. */
  sx?: SxProps<Theme>;
}
export const ImageBox = ({
  imageUrl,
  altText,
  variant = 'card',
  sizes,
  priority = false,
  objectFit = 'cover',
  unoptimized,
  sx
}: ImageBoxProps) => {
  // Keyed on the URL that failed rather than a plain flag, so a later prop
  // change gets a fresh attempt instead of inheriting the previous failure.
  const [failedUrl, setFailedUrl] = useState<ImageBoxProps['imageUrl']>();
  const loader = imageUrl ? getImageLoader(imageUrl) : undefined;
  const showImage = Boolean(imageUrl) && imageUrl !== failedUrl;
  return <Box sx={[theme => ({
    position: 'relative',
    overflow: 'hidden',
    display: 'block',
    width: '100%',
    aspectRatio: '1',
    borderRadius: variantRadius[variant](theme),
    // Only stands in for a missing image. Painting it underneath a
    // loaded one would tint anything with transparency, which callers
    // that do want a backdrop ask for through `sx` instead.
    ...(showImage ? {} : {
      backgroundColor: theme.palette.action.disabledBackground
    })
  }), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      {showImage && <NextImage src={imageUrl as string} alt={altText} fill sizes={sizes ?? variantSizes[variant]} loader={loader}
    // Without a loader there is nothing to resize the image, so pass the
    // original through rather than routing it via the framework's own
    // optimizer.
    unoptimized={unoptimized ?? !loader} priority={priority} loading={priority ? 'eager' : 'lazy'} onError={() => setFailedUrl(imageUrl)} style={{
      objectFit
    }} />}
    </Box>;
};